import { Suspense } from 'react';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';

import { ProviderWrapper } from '@/shared/providers';
import ErrorBoundary from '@/shared/components/ErrorBoundary';
import { useSubdomainValidation } from '@/shared/hooks/useSubdomainValidation';

// Route components
import MainDomainRoutes from '@/pages/routes/MainDomainRoutes';
import SubdomainRoutes from '@/pages/routes/SubdomainRoutes';

// Loading component for Suspense
const PageLoader = () => null;

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 5 * 60 * 1000, // 5 minutes default - data doesn't change frequently
      gcTime: 30 * 60 * 1000, // 30 minutes - keep data in cache longer
      refetchOnWindowFocus: false, // Prevent unnecessary refetches on window focus
      refetchOnMount: false, // Use cached data on component mount
      refetchOnReconnect: false, // Don't refetch on reconnect
      retry: 1, // Only retry once on failure
    },
  },
});

function AppRoutes() {
  const { isValidSubdomain, isLoading, hasSubdomain } = useSubdomainValidation();

  // Show loading while validating subdomain
  if (hasSubdomain && isLoading) {
    return <PageLoader />;
  }

  // Show subdomain routes if we're on a valid subdomain, otherwise main domain routes
  return isValidSubdomain ? <SubdomainRoutes /> : <MainDomainRoutes />;
}

export default function App() {
  return (
    <ErrorBoundary>
      <ProviderWrapper>
        <QueryClientProvider client={queryClient}>
          <Suspense fallback={<PageLoader />}>
            <AppRoutes />
          </Suspense>
        </QueryClientProvider>
      </ProviderWrapper>
    </ErrorBoundary>
  );
}
